import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  BackorderReport,
  OrgUnitReport,
  PipelineReport,
  ReceiptTypeReport,
  ReportRange,
  RequisitionReport,
  StockFlowReport,
  TopItemsReport,
  WarehouseReport,
} from '@eombor/contracts';
import type { ApiClientError } from '../../../shared/api/client.js';
import { reportApi, type SubdivisionReportPage } from '../api/report.api.js';

/**
 * Hisobot so'rovlari.
 *
 * Kalitga DAVR kiradi: sana o'zgarganda ro'yxat qayta so'raladi, lekin eski
 * javob keshda qoladi — «30 kun → 7 kun → 30 kun» ketma-ketligi bitta
 * so'rovga tushadi.
 */
export const reportKeys = {
  all: ['report'] as const,
  stockFlow: (range: ReportRange) => ['report', 'stock-flow', range.from, range.to] as const,
  topItems: (range: ReportRange) => ['report', 'top-items', range.from, range.to] as const,
  requisitions: (range: ReportRange) => ['report', 'requisitions', range.from, range.to] as const,
  subdivisions: (q: string, page: number) => ['report', 'subdivisions', q, page] as const,
  /* Davrsiz so'rovlar kalitida sana YO'Q — davr o'zgarganda ular qayta
     so'ralmasligi kerak, aks holda ekran sababsiz miltillardi. */
  warehouses: ['report', 'warehouses'] as const,
  receipts: (range: ReportRange) => ['report', 'receipts', range.from, range.to] as const,
  pipeline: ['report', 'pipeline'] as const,
  orgUnits: (range: ReportRange) => ['report', 'org-units', range.from, range.to] as const,
  backorders: ['report', 'backorders'] as const,
};

/**
 * Hisobot ma'lumoti bir necha daqiqa "yangi" hisoblanadi.
 *
 * Sabab — bu KUNLIK agregat: bir daqiqada o'zgarishi ehtimoli past, so'rov
 * esa qimmat (jurnal bo'yicha guruhlash). Talabnoma navbatidan farqli
 * o'laroq, bu yerda ekran ochiq turganda o'z-o'zidan yangilanish shart emas.
 */
const STALE_MS = 5 * 60_000;

export const useStockFlow = (range: ReportRange): UseQueryResult<StockFlowReport, ApiClientError> =>
  useQuery<StockFlowReport, ApiClientError>({
    queryKey: reportKeys.stockFlow(range),
    queryFn: () => reportApi.stockFlow(range),
    staleTime: STALE_MS,
  });

export const useTopItems = (range: ReportRange): UseQueryResult<TopItemsReport, ApiClientError> =>
  useQuery<TopItemsReport, ApiClientError>({
    queryKey: reportKeys.topItems(range),
    queryFn: () => reportApi.topItems(range),
    staleTime: STALE_MS,
  });

export const useRequisitionReport = (
  range: ReportRange,
): UseQueryResult<RequisitionReport, ApiClientError> =>
  useQuery<RequisitionReport, ApiClientError>({
    queryKey: reportKeys.requisitions(range),
    queryFn: () => reportApi.requisitions(range),
    staleTime: STALE_MS,
  });

export const useSubdivisionReport = (
  q: string,
  page: number,
): UseQueryResult<SubdivisionReportPage, ApiClientError> =>
  useQuery<SubdivisionReportPage, ApiClientError>({
    queryKey: reportKeys.subdivisions(q, page),
    queryFn: () => reportApi.subdivisions({ q, page }),
    staleTime: STALE_MS,
    /* Qidiruvda oldingi natija EKRANDA qoladi: har harfda ro'yxat
       skeletonga aylansa, ko'z tutib turolmaydi. */
    placeholderData: (previous) => previous,
  });

export const useWarehouseReport = (): UseQueryResult<WarehouseReport, ApiClientError> =>
  useQuery<WarehouseReport, ApiClientError>({
    queryKey: reportKeys.warehouses,
    queryFn: () => reportApi.warehouses(),
    staleTime: STALE_MS,
  });

export const useReceiptReport = (
  range: ReportRange,
): UseQueryResult<ReceiptTypeReport, ApiClientError> =>
  useQuery<ReceiptTypeReport, ApiClientError>({
    queryKey: reportKeys.receipts(range),
    queryFn: () => reportApi.receipts(range),
    staleTime: STALE_MS,
  });

export const usePipelineReport = (): UseQueryResult<PipelineReport, ApiClientError> =>
  useQuery<PipelineReport, ApiClientError>({
    queryKey: reportKeys.pipeline,
    queryFn: () => reportApi.pipeline(),
    staleTime: STALE_MS,
  });

export const useOrgUnitReport = (
  range: ReportRange,
): UseQueryResult<OrgUnitReport, ApiClientError> =>
  useQuery<OrgUnitReport, ApiClientError>({
    queryKey: reportKeys.orgUnits(range),
    queryFn: () => reportApi.orgUnits(range),
    staleTime: STALE_MS,
  });

export const useBackorderReport = (): UseQueryResult<BackorderReport, ApiClientError> =>
  useQuery<BackorderReport, ApiClientError>({
    queryKey: reportKeys.backorders,
    queryFn: () => reportApi.backorders(),
    staleTime: STALE_MS,
  });
